import { Sparkles, Plus } from 'lucide-react';
import { CardImage } from '@/components/CardImage';
import { InkDot } from '@/components/InkDot';
import { useDeckSuggestions } from '@/hooks/useDeckSuggestions';
import type { Card } from '@/hooks/useCards';
import type { InkColor } from '@/lib/ink-utils';

interface DeckSuggestionsProps {
  deckId: string;
  currentCardIds: string[];
  allowedInks: InkColor[];
  onAdd: (card: Card) => void;
}

export function DeckSuggestions({ deckId, currentCardIds, allowedInks, onAdd }: DeckSuggestionsProps) {
  const { data: suggestions, isLoading } = useDeckSuggestions(deckId, currentCardIds);

  // Filter to deck's allowed inks
  const filtered = (suggestions || []).filter(c => allowedInks.includes(c.ink_color as InkColor)).slice(0, 8);

  if (isLoading) return null;
  if (!filtered.length) return null;

  return (
    <div className="rounded-xl border border-border bg-card p-3 mb-3 animate-fade-in">
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="h-3.5 w-3.5 text-primary" />
        <span className="text-xs font-medium text-foreground font-body">Players also run</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {filtered.map(card => (
          <button
            key={card.id}
            onClick={() => onAdd(card)}
            className="group flex-shrink-0 w-16 text-left"
          >
            <div className="relative">
              <CardImage src={card.image_url} alt={card.name} inkColor={card.ink_color as InkColor} className="w-16 aspect-[2/3] rounded transition-transform group-hover:scale-105" />
              <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
                <Plus className="h-3 w-3" />
              </span>
            </div>
            <div className="mt-1 flex items-center gap-0.5">
              <InkDot ink={card.ink_color as InkColor} size="sm" />
              <span className="text-[9px] text-muted-foreground font-body truncate">{card.name}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
